import { resolveInvoiceQr } from './invoiceQr'

export const invoicePrintSnap = (obj, keys, fallback = '') => {
  const source = obj || {}
  for (const key of keys) {
    const value = source[key]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return fallback
}

export const invoicePrintBusinessValue = (snapshot, keys, fallback = '') => {
  for (const source of [
    snapshot,
    snapshot?.business_info,
    snapshot?.business
  ]) {
    const value = invoicePrintSnap(source, keys, '')
    if (value !== '') return value
  }
  return fallback
}

export const invoicePrintSnapshotName = (snapshot, fallback = '') => {
  const keys = [
    'name',
    'legal_name',
    'legal_name_en',
    'name_en',
    'location_name',
    'code',
    'email',
    'phone'
  ]
  return (
    invoicePrintSnap(snapshot, keys, '') ||
    invoicePrintSnap(
      snapshot?.business_info || snapshot?.business,
      keys,
      fallback
    )
  )
}

const esc = value => {
  if (value == null || value === '') return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

const fmtNum = value => {
  const number = Number(value)
  if (Number.isNaN(number)) return '0.00'
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const fmtDate = value => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const pad = part => String(part).padStart(2, '0')
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(
    date.getDate()
  )}`
}

const fmtTime = value => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = part => String(part).padStart(2, '0')
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const partyInfo = inv => {
  const seller = inv.seller_snapshot || {}
  const buyer = inv.buyer_snapshot || {}
  const sellingLocation = inv.selling_location_snapshot || {}

  return {
    seller,
    buyer,
    sellingLocation,
    sellerName: invoicePrintSnapshotName(seller, inv.seller_name || ''),
    buyerName: invoicePrintSnap(
      buyer,
      ['name', 'buyer_name'],
      inv.buyer_name || ''
    ),
    buyerPhone: invoicePrintSnap(
      buyer,
      ['phone', 'buyer_phone'],
      inv.buyer_phone || ''
    ),
    buyerCrNumber: invoicePrintSnap(
      buyer,
      ['cr_number', 'buyer_cr_number', 'commercial_registration'],
      inv.buyer_cr_number || ''
    ),
    buyerVatNumber: invoicePrintSnap(
      buyer,
      ['vat_number', 'buyer_vat_number', 'tax_number'],
      inv.buyer_vat_number || ''
    ),
    buyerAddress: invoicePrintSnap(
      buyer,
      ['address', 'buyer_address'],
      inv.buyer_address || ''
    ),
    sellingLocationName: invoicePrintSnapshotName(
      sellingLocation,
      inv.selling_location_name || inv.location_name || ''
    ),
    preparedBy: inv.prepared_by_name || inv.created_by_name || ''
  }
}

const detailRow = (label, value, arLabel) => `
  <div class="detail-row">
    <span>${label}</span>
    <strong>${esc(value) || '&nbsp;'}</strong>
    <span class="ar">${arLabel}</span>
  </div>`

export async function buildSaleInvoicePrintHtml (inv, lang = 'en', mode = '') {
  const items = inv.items || []
  const party = partyInfo(inv)
  const sellerNameEn = invoicePrintBusinessValue(
    party.seller,
    ['name_en', 'legal_name_en', 'seller_name', 'name'],
    party.sellerName
  )
  const sellerNameAr = invoicePrintBusinessValue(
    party.seller,
    ['name_ar', 'legal_name_ar', 'arabic_name'],
    sellerNameEn
  )
  const sellerCr = invoicePrintBusinessValue(
    party.seller,
    ['cr_number', 'commercial_registration', 'registration_number'],
    ''
  )
  const sellerVat = invoicePrintBusinessValue(
    party.seller,
    ['vat_number', 'tax_number', 'vat_no', 'tin'],
    ''
  )
  const sellerPhone = invoicePrintBusinessValue(
    party.seller,
    ['phone', 'telephone', 'mobile'],
    ''
  )
  const sellerFax = invoicePrintBusinessValue(party.seller, ['fax'], '')
  const sellerAddress = invoicePrintBusinessValue(
    party.seller,
    ['address', 'address_en'],
    ''
  )
  const sellerAddressAr = invoicePrintBusinessValue(
    party.seller,
    ['address_ar'],
    ''
  )
  const invoiceLogos = [
    invoicePrintSnap(
      inv,
      ['logo_url'],
      invoicePrintBusinessValue(
        party.seller,
        ['logo_url', 'logo', 'business_logo', 'tenant_logo'],
        ''
      )
    ),
    invoicePrintSnap(
      inv,
      ['logo2'],
      invoicePrintBusinessValue(party.seller, ['logo2'], '')
    )
  ].filter(Boolean)
  const extraNo = invoicePrintBusinessValue(party.seller, ['extra_no'], '')
  const printDate = new Date()
  const qrImage = await resolveInvoiceQr(inv, party.seller)
  const refunds = inv.refunds || []
  const totalRefunded = refunds.reduce(
    (sum, refund) => sum + (Number(refund.amount) || 0),
    0
  )
  const statusLabel =
    mode ||
    (inv.status === 'CANCELLED'
      ? 'CANCELLED'
      : inv.is_refunded
      ? 'REFUNDED'
      : '')
  const densityClass =
    items.length > 16 ? ' dense' : items.length > 9 ? ' compact' : ''
  const itemsHtml = items.length
    ? items
        .map((line, index) => {
          const qty = Number(line.quantity || 0)
          const price = Number(line.unit_price || 0)
          const total = Number(line.total_price ?? qty * price)
          const taxable = Number(line.taxable_amount ?? total)
          const vatAmount = Number(line.vat_amount || 0)
          const net = taxable + vatAmount
          const isRefunded = line.item_status === 'REFUNDED'

          return `<tr class="${isRefunded ? 'refunded' : ''}">
            <td>${index + 1}</td>
            <td>${esc(line.item_code || line.sku_code || '')}</td>
            <td class="item-name">${esc(
              line.item_name || line.item_code || ''
            )}${isRefunded ? ' (REFUNDED)' : ''}</td>
            <td>${esc(line.uom_name || line.uom || '')}</td>
            <td class="num">${fmtNum(qty)}</td>
            <td class="num">${fmtNum(price)}</td>
            <td class="num">${fmtNum(total)}</td>
            <td class="num">${fmtNum(line.discount || 0)}</td>
            <td class="num">${fmtNum(taxable)}</td>
            <td class="num">${
              line.vat_applied === false
                ? '—'
                : line.vat_pct ?? line.vat_rate ?? inv.vat_rate ?? 0
            }</td>
            <td class="num">${fmtNum(vatAmount)}</td>
            <td class="num">${fmtNum(net)}</td>
          </tr>`
        })
        .join('')
    : '<tr><td colspan="12" class="empty">No items</td></tr>'

  return `<!DOCTYPE html>
<html lang="${esc(lang)}">
<head>
<meta charset="UTF-8" />
<title>Sale Invoice - ${esc(inv.invoice_number)}</title>
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{background:#fff;color:#111;font-family:Arial,Tahoma,sans-serif;font-size:9.8px;line-height:1.26}
.page{width:216mm;min-height:279mm;margin:0 auto;padding:5mm}
.sheet{min-height:269mm;border:1px solid #333;display:flex;flex-direction:column}
.head{display:grid;grid-template-columns:1fr 58mm 1fr;align-items:center;min-height:34mm;border-bottom:1px solid #333;padding:2.4mm 3mm;gap:3mm}
.seller{font-size:10.8px;font-weight:700;line-height:1.34}.seller.ar{text-align:right;direction:rtl}.seller .muted{font-weight:600}.seller div{white-space:nowrap}
.logos{display:flex;align-items:center;justify-content:center;gap:2mm}.logos img{width:26mm;height:27mm;object-fit:contain}.logos img:only-child{width:54mm}
.title-line{display:grid;grid-template-columns:26mm 1fr 26mm;align-items:center;border-bottom:1px solid #333;min-height:6.5mm;text-align:center;font-size:11.5px;font-weight:700}
.serial{border-inline-end:1px solid #333;height:100%;display:flex;align-items:center;justify-content:center;gap:4mm}
.copy{border-inline-start:1px solid #333;height:100%;display:flex;align-items:center;justify-content:center}
.content-grid{display:grid;grid-template-columns:35mm 1fr 1fr;border-bottom:1px solid #333}
.qr-box{border-inline-end:1px solid #333;padding:3mm 2mm;display:flex;align-items:flex-start;justify-content:center}.qr-box img{width:31mm;height:31mm;object-fit:contain}
.panel{border-inline-end:1px solid #333}.panel:last-child{border-inline-end:0}
.panel-title{display:grid;grid-template-columns:1fr 1fr;gap:2mm;padding:1.2mm 2mm;border-bottom:1px solid #999;font-size:9.4px;font-weight:700}.panel-title .ar{text-align:right;direction:rtl}
.detail-row{display:grid;grid-template-columns:26mm 1fr 28mm;gap:1.5mm;align-items:center;min-height:5.7mm;padding:.55mm 2mm;border-bottom:1px dotted #bbb}
.detail-row:last-child{border-bottom:0}.detail-row strong{text-align:center;font-size:9.6px;overflow-wrap:anywhere}.detail-row span:first-child{font-weight:600}.detail-row .ar{text-align:right;direction:rtl;font-weight:700}
.state{border-bottom:1px solid #333;color:#9b111e;text-align:center;font-size:12px;font-weight:800;padding:1mm}
table{width:100%;border-collapse:collapse}.items{table-layout:fixed}.items th,.items td{border:1px solid #555;padding:.8mm .55mm;text-align:center;vertical-align:middle}.items th{font-size:8.1px;font-weight:700;line-height:1.16}.items td{font-size:8.7px;font-weight:500}.items .item-name{text-align:right;direction:ltr}.items .num{text-align:right;font-variant-numeric:tabular-nums}.items .refunded td{text-decoration:line-through;color:#777}.items .empty{padding:8mm 0;color:#666}
.items th:nth-child(1){width:5mm}.items th:nth-child(2){width:15mm}.items th:nth-child(3){width:42mm}.items th:nth-child(4){width:10mm}.items th:nth-child(5){width:14mm}.items th:nth-child(6),.items th:nth-child(7),.items th:nth-child(8),.items th:nth-child(9),.items th:nth-child(11),.items th:nth-child(12){width:16mm}.items th:nth-child(10){width:8mm}
.spacer{flex:1;min-height:55mm;border-bottom:1px solid #333;position:relative}.stamp-space{position:absolute;left:52%;top:42%;transform:translate(-50%,-50%);width:58mm;height:31mm;border:1px dashed transparent}
.bottom-grid{display:grid;grid-template-columns:91mm 1fr;border-bottom:1px solid #333;min-height:27mm}
.totals{border-inline-end:1px solid #333}.totals td{border:1px solid #777;padding:.9mm 1.5mm;font-size:9.6px}.totals td:first-child{width:30mm;text-align:right;font-weight:700}.totals td:nth-child(2){font-weight:700}.totals td:nth-child(3){text-align:right;direction:rtl;font-weight:700}.totals .amount{font-variant-numeric:tabular-nums}
.notes{display:grid;grid-template-rows:repeat(4,1fr)}.note-row{display:grid;grid-template-columns:1fr 29mm;align-items:center;border-bottom:1px solid #aaa;min-height:6.2mm}.note-row:last-child{border-bottom:0}.note-row span{padding:0 2mm;text-align:right;font-weight:700}.note-row b{height:100%;border-inline-start:1px solid #aaa;padding:1mm 2mm;text-align:right;direction:rtl;font-size:9.4px}
.amount-words{display:grid;grid-template-columns:1fr 40mm;border-bottom:1px solid #333;min-height:7.5mm}.amount-words div{padding:1.2mm 2mm;font-size:9.5px}.amount-words .label{text-align:right;direction:rtl;border-inline-start:1px solid #333;font-weight:700}
.print-footer{display:grid;grid-template-columns:1fr 1fr 1fr;align-items:center;font-size:9px;padding:1.2mm 2mm}.print-footer .center{text-align:center}.print-footer .right{text-align:right;direction:rtl}
.compact .items th{font-size:7.4px}.compact .items td{font-size:8px;padding:.62mm .38mm}.compact .spacer{min-height:48mm}.dense .head{min-height:30mm}.dense .seller{font-size:9.2px}.dense .logos img{width:23mm;height:22mm}.dense .logos img:only-child{width:48mm}.dense .content-grid{grid-template-columns:31mm 1fr 1fr}.dense .qr-box img{width:27mm;height:27mm}.dense .detail-row{min-height:4.7mm}.dense .items th{font-size:6.8px}.dense .items td{font-size:7.3px;padding:.42mm .25mm}.dense .spacer{min-height:40mm}
@media print{@page{size:letter portrait;margin:0}html,body{width:216mm;min-height:279mm}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.page{margin:0}}
</style>
</head>
<body>
<div class="page${densityClass}">
  <div class="sheet">
    <div class="head">
      <div class="seller">${esc(sellerNameEn)}<br />${esc(sellerAddress)}<br />
        ${extraNo ? `Extra No \\ ${esc(extraNo)}<br />` : ''}
        ${sellerPhone ? `Phone No \\ ${esc(sellerPhone)} &nbsp;&nbsp;` : ''}${
    sellerFax ? `Fax No \\ ${esc(sellerFax)}<br />` : ''
  }
        ${sellerVat ? `VAT No \\ ${esc(sellerVat)}<br />` : ''}${
    sellerCr ? `CRN No \\ ${esc(sellerCr)}` : ''
  }
      </div>
      <div class="logos">${invoiceLogos
        .map(
          (logo, index) =>
            `<img src="${esc(logo)}" alt="${
              index ? 'Partner logo' : 'Company logo'
            }" />`
        )
        .join('')}</div>
      <div class="seller ar">${esc(sellerNameAr)}<br />${esc(
    sellerAddressAr
  )}<br />
        ${extraNo ? `رقم إضافي \\ ${esc(extraNo)}<br />` : ''}
        ${sellerPhone ? `رقم الهاتف \\ ${esc(sellerPhone)}<br />` : ''}${
    sellerVat ? `رقم السجل الضريبي \\ ${esc(sellerVat)}<br />` : ''
  }${sellerCr ? `رقم السجل التجاري \\ ${esc(sellerCr)}` : ''}
      </div>
    </div>
    <div class="title-line"><div class="serial"><span>رقم النسخة</span></div><div>Tax Invoice / فاتورة ضريبية</div><div class="copy"></div></div>
    ${statusLabel ? `<div class="state">${esc(statusLabel)}</div>` : ''}
    <div class="content-grid">
      <div class="qr-box">${
        qrImage ? `<img src="${esc(qrImage)}" alt="Invoice QR" />` : ''
      }</div>
      <div class="panel">
        <div class="panel-title"><span>Information Customer :</span><span class="ar">معلومات العميل :</span></div>
        ${detailRow('Name', party.buyerName, 'اسم العميل')}
        ${detailRow('Address', party.buyerAddress, 'عنوان العميل')}
        ${detailRow('Tax No', party.buyerVatNumber, 'الرقم الضريبي')}
        ${detailRow('Other ID', party.buyerCrNumber, 'معرف آخر')}
      </div>
      <div class="panel">
        <div class="panel-title"><span>Information Invoice :</span><span class="ar">معلومات الفاتورة :</span></div>
        ${detailRow('Invoice No', inv.invoice_number, 'رقم الفاتورة')}
        ${detailRow(
          'Invoice Date',
          `${fmtTime(inv.created_at)}  ${fmtDate(inv.created_at)}`,
          'تاريخ الفاتورة'
        )}
        ${detailRow('Invoice Type', inv.payment_method, 'نوع الفاتورة')}
        ${detailRow('Saleman Na', party.preparedBy, 'اسم المندوب')}
        ${detailRow('Cost Center', party.sellingLocationName, 'مركز التكلفة')}
        ${detailRow(
          'Date Supply',
          fmtDate(inv.supply_date || inv.created_at),
          'تاريخ التوريد'
        )}
      </div>
    </div>
    <table class="items">
      <thead><tr>
        <th>No.</th><th>Item No</th><th>Item Name<br />اسم الصنف</th><th>Unit<br />الوحدة</th><th>Sale Qty<br />الكمية</th><th>Price<br />السعر</th><th>Total<br />الإجمالي</th><th>Discount<br />الخصم</th><th>Taxable Amount<br />المبلغ الخاضع للضريبة</th><th>VAT<br />%</th><th>VAT Amount<br />قيمة الضريبة</th><th>Net Total<br />الإجمالي شامل الضريبة</th>
      </tr></thead>
      <tbody>${itemsHtml}</tbody>
    </table>
    <div class="spacer"><div class="stamp-space"></div></div>
    <div class="bottom-grid">
      <table class="totals">
        <tbody>
          <tr><td class="amount">${fmtNum(
            inv.gross_total
          )}</td><td>(Total Excl.VAT)</td><td>المجموع قبل الضريبة</td></tr>
          <tr><td class="amount">${fmtNum(
            inv.discount || 0
          )}</td><td>(Deduction)</td><td>إجمالي الخصم</td></tr>
          <tr><td class="amount">${fmtNum(
            inv.vat_amount || 0
          )}</td><td>(VAT Amount)</td><td>ضريبة القيمة المضافة</td></tr>
          <tr><td class="amount">${fmtNum(
            inv.grand_total
          )}</td><td>(Amount Incl.VAT)</td><td>الإجمالي بعد الضريبة</td></tr>
          ${
            totalRefunded > 0
              ? `<tr><td class="amount">-${fmtNum(
                  totalRefunded
                )}</td><td>(Refunded)</td><td>المسترد</td></tr>`
              : ''
          }
        </tbody>
      </table>
      <div class="notes">
        <div class="note-row"><span>${esc(
          inv.notes || inv.note || ''
        )}</span><b>(Notes) ملاحظات</b></div>
        <div class="note-row"><span>${esc(
          inv.carrier_name || inv.carrier || ''
        )}</span><b>(Carrier) الشحن</b></div>
        <div class="note-row"><span>${esc(
          inv.item_count || ''
        )}</span><b>إجمالي الصنف</b></div>
        <div class="note-row"><span>${esc(
          inv.grand_total_words || ''
        )}</span><b>المبلغ كتابة</b></div>
      </div>
    </div>
    <div class="amount-words"><div>${esc(
      inv.grand_total_words || ''
    )}</div><div class="label">المبلغ كتابة</div></div>
    <div class="print-footer"><div></div><div class="center">${
      party.preparedBy ? `المستخدم: ${esc(party.preparedBy)} &nbsp;` : ''
    }${fmtTime(printDate)} &nbsp; ${fmtDate(
    printDate
  )}</div><div class="right">تاريخ الطباعة :</div></div>
  </div>
</div>
</body>
</html>`
}
